import { ConnectorNotRegisteredError } from './ConnectorErrors'
import type { ConnectorId, IConnector } from './ConnectorTypes'

export type ConnectorConstructor = new () => IConnector

/**
 * Known supplier keys the factory is prepared to host.
 * Implementations are registered later — Sprint 2.4 ships SDK only.
 */
export const KNOWN_CONNECTOR_IDS = [
  'pauta',
  'agis',
  'all-nations',
  'ingram',
  'oderco',
  'snd',
  'kabum',
  'terabyte',
  'pichau',
  'amazon',
  'mercado-livre',
  'magazine-luiza',
] as const

export type KnownConnectorId = (typeof KNOWN_CONNECTOR_IDS)[number]

/**
 * ConnectorFactory — single place to instantiate connectors by id.
 * Suppliers must register a class that extends BaseConnector / implements IConnector.
 */
export class ConnectorFactory {
  private readonly registry = new Map<string, ConnectorConstructor>()

  /** Register a connector implementation for a given id. */
  register(id: ConnectorId | KnownConnectorId, ctor: ConnectorConstructor): void {
    this.registry.set(normalizeId(id), ctor)
  }

  unregister(id: ConnectorId | KnownConnectorId): boolean {
    return this.registry.delete(normalizeId(id))
  }

  has(id: ConnectorId | KnownConnectorId): boolean {
    return this.registry.has(normalizeId(id))
  }

  listRegistered(): string[] {
    return [...this.registry.keys()].sort()
  }

  listKnown(): readonly KnownConnectorId[] {
    return KNOWN_CONNECTOR_IDS
  }

  /**
   * Instantiate a connector by id.
   * Throws ConnectorNotRegisteredError until a supplier SDK class is registered.
   */
  create(id: ConnectorId | KnownConnectorId): IConnector {
    const key = normalizeId(id)
    const ctor = this.registry.get(key)
    if (!ctor) {
      throw new ConnectorNotRegisteredError(key)
    }
    return new ctor()
  }

  /** Safe create — returns null when not registered. */
  tryCreate(id: ConnectorId | KnownConnectorId): IConnector | null {
    try {
      return this.create(id)
    } catch (error) {
      if (error instanceof ConnectorNotRegisteredError) return null
      throw error
    }
  }

  clear(): void {
    this.registry.clear()
  }
}

function normalizeId(id: string): string {
  return id.trim().toLowerCase()
}

export const connectorFactory = new ConnectorFactory()

/** Convenience aliases matching sprint naming. */
export function createPautaConnector(): IConnector {
  return connectorFactory.create('pauta')
}

export function createAgisConnector(): IConnector {
  return connectorFactory.create('agis')
}

export function createAllNationsConnector(): IConnector {
  return connectorFactory.create('all-nations')
}

export function createIngramConnector(): IConnector {
  return connectorFactory.create('ingram')
}
